import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Modal, ModalContent, ModalFooter, ModalHeader } from 'app/components/shared/Modal';

const ConfirmUnsavedChangesModal = ({ hasUnsavedChanges }) => {
  const history = useHistory();
  const { location } = history;

  const unblockRef = useRef(null);

  const [state, setState] = useState({
    currentPathname: location.pathname,
    destinationPathname: null,
    isModalOpen: false
  });

  const { isModalOpen, currentPathname, destinationPathname } = state;

  const closeModal = () => {
    setState(prevState => {
      return {
        ...prevState,
        isModalOpen: false
      };
    });
  };

  useEffect(() => {
    setState(prevState => {
      if (prevState.currentPathname !== location.pathname) {
        return {
          ...prevState,
          currentPathname: location.pathname
        };
      }
      return prevState;
    });
  }, [location.pathname]);

  // handle back-button or react router navigation
  useEffect(() => {
    if (hasUnsavedChanges) {
      unblockRef.current = history.block(newLocation => {
        const { pathname: newDestinationPathname } = newLocation;
        if (newDestinationPathname === currentPathname) {
          return true;
        }

        setState(prevState => {
          return {
            ...prevState,
            destinationPathname: newDestinationPathname,
            isModalOpen: true
          };
        });

        return false;
      });

      return () => {
        unblockRef.current();
      };
    }

    return () => {};
  }, [currentPathname, history, hasUnsavedChanges]);

  // add page reload listener
  useEffect(() => {
    if (hasUnsavedChanges) {
      const handler = event => {
        event.preventDefault();
        event.returnValue = true;
      };

      const EVENT_NAME = 'beforeunload';

      window.addEventListener(EVENT_NAME, handler);

      return () => window.removeEventListener(EVENT_NAME, handler);
    }

    return () => {};
  }, [hasUnsavedChanges]);

  const handleConfirm = () => {
    if (unblockRef.current) {
      unblockRef.current();
    }
    history.push(destinationPathname);
  };

  return (
    <Modal handleClose={closeModal} key="ConfirmUnsavedChangesModal" open={isModalOpen}>
      <ModalHeader title="Unsaved changes" />
      <ModalContent>
        <p>
          Are you sure you want to go back? You have unsaved changes on this page that will be lost if you go back
          without saving.
        </p>
      </ModalContent>
      <ModalFooter handlePrimaryAction={handleConfirm} primaryActionButtonText="Yes, go back" />
    </Modal>
  );
};

ConfirmUnsavedChangesModal.propTypes = {
  hasUnsavedChanges: PropTypes.bool.isRequired
};

export default ConfirmUnsavedChangesModal;
